import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Gauge, HelpCircle, LogOut, Menu, Search, UserRound, X } from 'lucide-react';
import CommandPalette from './CommandPalette';
import HelpDrawer from './HelpDrawer';
import { IconButton, Tooltip } from './ui';
import { getRouteMeta, QUICK_ROUTE_IDS, routeRegistry, routesBySection } from '../config/routeRegistry';
import axios from 'axios';
import { setStaffAccessToken, STAFF_API } from '../staffAuth';
import { queryClient } from '../queryClient';
import { useStaffNotifications } from '../staff/NotificationHub';

const COLLAPSED_KEY = 'vroom-panel-sidebar-collapsed';
const RECENT_KEY = 'vroom-panel-recent-routes';

const readJson = (key, fallback) => {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
};

function NavItem({ route, collapsed, onNavigate, count = 0 }) {
  const Icon = route.icon;
  const link = (
    <NavLink to={route.path} end={route.path === '/'} onClick={onNavigate} className={({ isActive }) => `cc-nav-link ${isActive ? 'is-active' : ''}`}>
      <Icon size={17} strokeWidth={1.8} aria-hidden="true" />
      <span>{route.label}</span>
      {count > 0 && <b className="cc-nav-count" aria-label={`${count} spraw`}>{count > 99 ? '99+' : count}</b>}
    </NavLink>
  );
  return collapsed ? <Tooltip content={`${route.label}${count ? ` · ${count}` : ''}`} side="right">{link}</Tooltip> : link;
}

export default function Layout() {
  const { counts } = useStaffNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => readJson(COLLAPSED_KEY, false));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [recentIds, setRecentIds] = useState(() => readJson(RECENT_KEY, []));
  const currentRoute = useMemo(() => getRouteMeta(location.pathname), [location.pathname]);
  const quickRoutes = QUICK_ROUTE_IDS.map((id) => routeRegistry.find((route) => route.id === id)).filter(Boolean);
  const recentRoutes = recentIds.map((id) => routeRegistry.find((route) => route.id === id)).filter(Boolean);

  useEffect(() => { localStorage.setItem(COLLAPSED_KEY, JSON.stringify(collapsed)); }, [collapsed]);
  useEffect(() => {
    if (!currentRoute?.id) return;
    setRecentIds((previous) => {
      const next = [currentRoute.id, ...previous.filter((id) => id !== currentRoute.id)].slice(0, 5);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      return next;
    });
    setMobileOpen(false);
  }, [currentRoute?.id]);
  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') { event.preventDefault(); setPaletteOpen(true); }
      if (event.key === '?' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) setHelpOpen(true);
    };
    const onHelp = () => setHelpOpen(true);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('vroom:help', onHelp);
    return () => { window.removeEventListener('keydown', onKeyDown); window.removeEventListener('vroom:help', onHelp); };
  }, []);

  const logout = async () => {
    try { await axios.post(`${STAFF_API}/api/staff/auth/logout`, {}); } catch { /* Kończymy sesję lokalnie także przy braku sieci. */ }
    setStaffAccessToken(null);
    queryClient.clear();
    navigate('/login');
  };
  const closeMobile = () => setMobileOpen(false);

  return (
    <div className={`cc-shell ${collapsed ? 'is-collapsed' : ''} ${mobileOpen ? 'is-mobile-open' : ''}`} style={{ '--cc-sidebar-width': collapsed ? '0px' : '280px' }}>
      {mobileOpen && <button type="button" className="cc-mobile-overlay" onClick={closeMobile} aria-label="Zamknij nawigację" />}
      <aside className="cc-sidebar" aria-label="Nawigacja główna">
        <div className="cc-sidebar__brand">
          <div className="cc-brand__mark"><Gauge aria-hidden="true" /></div>
          <div className="cc-brand__copy"><div className="cc-brand__word">VROOM</div><small>Command Center</small></div>
          <IconButton className="cc-sidebar__collapse" label={collapsed ? 'Rozwiń sidebar' : 'Zwiń sidebar'} icon={collapsed ? ChevronRight : ChevronLeft} size="sm" onClick={() => setCollapsed((value) => !value)} />
          <IconButton className="cc-topbar__mobile" label="Zamknij menu" icon={X} onClick={closeMobile} />
        </div>

        <button type="button" className="cc-sidebar__search" onClick={() => setPaletteOpen(true)} aria-label="Wyszukaj moduł">
          <Search size={16} aria-hidden="true" /><span>Wyszukaj moduł</span><kbd>⌘ K</kbd>
        </button>

        <div className="cc-sidebar__scroll">
          <nav>
            <section className="cc-nav-section" aria-labelledby="quick-nav-label">
              <div className="cc-nav-section__label" id="quick-nav-label">Skróty</div>
              {quickRoutes.map((route) => <NavItem key={route.id} route={route} collapsed={collapsed} onNavigate={closeMobile} count={counts[route.id]} />)}
            </section>
            {!collapsed && recentRoutes.length > 0 && <section className="cc-nav-section" aria-labelledby="recent-nav-label"><div className="cc-nav-section__label" id="recent-nav-label">Ostatnio otwierane</div>{recentRoutes.map((route) => <NavItem key={`recent-${route.id}`} route={route} collapsed={false} onNavigate={closeMobile} count={counts[route.id]} />)}</section>}
            {routesBySection.map((section) => <section className="cc-nav-section" key={section.id} aria-labelledby={`section-${section.id}`}><div className="cc-nav-section__label" id={`section-${section.id}`}>{section.label}</div>{section.routes.filter((route) => !QUICK_ROUTE_IDS.includes(route.id)).map((route) => <NavItem key={route.id} route={route} collapsed={collapsed} onNavigate={closeMobile} count={counts[route.id]} />)}</section>)}
          </nav>
        </div>

        <div className="cc-sidebar__footer">
          <div className="cc-admin-avatar"><UserRound size={16} /></div>
          <div className="cc-sidebar__footer-copy"><strong>Administrator</strong><small>Sesja aktywna</small></div>
          <IconButton label="Wyloguj się" tooltip="Zakończ bezpiecznie sesję" icon={LogOut} onClick={logout} />
        </div>
      </aside>

      <div className="cc-workspace">
        <header className="cc-topbar">
          <IconButton className="cc-topbar__mobile" label="Otwórz menu" icon={Menu} onClick={() => setMobileOpen(true)} />
          {collapsed && <IconButton className="cc-sidebar-reopen" label="Pokaż menu" tooltip="Pokaż całe menu" icon={Menu} onClick={() => setCollapsed(false)} />}
          <div className="cc-topbar__route"><span className="cc-topbar__crumb">Command Center / {routesBySection.find((section) => section.id === currentRoute.section)?.label || 'Start'}</span><strong>{currentRoute.label}</strong></div>
          <button type="button" className="cc-topbar__search" onClick={() => setPaletteOpen(true)}><Search size={16} /><span>Wyszukaj moduł lub działanie…</span><kbd>Ctrl K</kbd></button>
          <div className="cc-topbar__status"><i /><span>Administrator online</span></div>
          <IconButton label="Pomoc dla modułu" tooltip={`Jak działa: ${currentRoute.label}`} icon={HelpCircle} onClick={() => setHelpOpen(true)} />
        </header>
        <main className="cc-content"><Outlet /></main>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} recentIds={recentIds} />
      <HelpDrawer open={helpOpen} onOpenChange={setHelpOpen} route={currentRoute} />
    </div>
  );
}
